export enum ELms {
    CANVAS = 'canvas',
    BLACKBOARD = 'blackboard',
    MOODLE = 'moodle',
    SCHOOLOGY = 'schoology',
    D2L = 'd2l'
}

/** The GMetrix LTI 1.1 Tool / launch URL (per GMetrixLTIIntegrationV3.pdf). */
export const GMETRIX_LTI_LAUNCH_URL = 'https://www.gmetrix.net/lti/';

/** Knowledge Base LTI setup overview, linked from the Integrations page help banner. */
export const GMETRIX_LTI_SETUP_OVERVIEW_URL = 'https://support.gmetrix.net/en/support/solutions/articles/67000755612-learning-tools-interoperability-lti-';

/**
 * Route suffixes for the GMetrix LTI 1.3 (Advantage) tool endpoints, used when registering the tool
 * **manually** (i.e. when the LMS doesn't support dynamic registration, such as self-hosted Canvas
 * or Moodle). They are
 * appended to the environment-provided base URL (`ConsumerSettings.AdvantageToolBaseURL`, the same
 * base used to build the dynamic-registration URL), which is also the tool domain. The redirect URI
 * and target-link (launch) URI share the same route. No static public key is needed — the LMS
 * fetches GMetrix's keys from the JWKS endpoint.
 */
export const GMETRIX_LTI13_LOGIN_PATH = 'OidcLogin';
export const GMETRIX_LTI13_REDIRECT_PATH = 'Launch';
export const GMETRIX_LTI13_TARGET_LINK_PATH = 'Launch';
export const GMETRIX_LTI13_JWKS_PATH = '.well-known/openid-configuration/jwks';

/**
 * The tool's deep-linking (content selection) route — where the LMS sends an
 * LtiDeepLinkingRequest so the instructor can pick a specific GMetrix test or course while
 * creating the link. It is a SEPARATE route from the launch endpoint above: the tool's OIDC login
 * step echoes the incoming target_link_uri back as the OAuth redirect_uri, so the LMS must be
 * configured with this URL for its content-selection placement or the picker never opens and every
 * link launches the generic course context instead. Manual provisioning already registers this
 * route tool-side (PartnersController.ProvisionManualLtiPlatform stores both /Launch and
 * /ResourceSelection as the tool redirect URIs), so only the LMS side needs the value.
 */
export const GMETRIX_LTI13_DEEP_LINK_PATH = 'ResourceSelection';

export type Lti13Endpoints = {
    deepLinkUrl: string;
    domain: string;
    jwksUrl: string;
    loginUrl: string;
    redirectUrl: string;
    targetLinkUrl: string;
};

/**
 * Builds the GMetrix LTI 1.3 (Advantage) tool endpoints from the environment-provided base URL
 * (`ConsumerSettings.AdvantageToolBaseURL`) by appending the route suffixes above. Returns empty
 * strings when no base URL is provisioned.
 */
export const buildLti13Endpoints = (advantageToolBaseURL?: string): Lti13Endpoints => {

    const base = advantageToolBaseURL ?? '';

    // AdvantageToolBaseURL points at the dynamic-registration ("init") endpoint — e.g.
    // https://host/clientregistrationinit/ — which is the ONLY URL that needs that path segment.
    // The manual LTI 1.3 endpoints (login init, redirect, JWKS, domain) live at the tool's ROOT
    // host, so derive the origin and hang the route suffixes off that, not off the init path.
    const origin = (() => {

        if (!base) return '';

        try {

            return new URL(base).origin;

        } catch {

            const match = base.match(/^(https?:\/\/[^/]+)/i);
            return match ? match[1] : base.replace(/\/+$/, '');

        }

    })();

    const join = (suffix: string) => (origin ? `${origin}/${suffix}` : '');

    return {
        deepLinkUrl: join(GMETRIX_LTI13_DEEP_LINK_PATH),
        domain: origin ? `${origin}/` : '',
        jwksUrl: join(GMETRIX_LTI13_JWKS_PATH),
        loginUrl: join(GMETRIX_LTI13_LOGIN_PATH),
        redirectUrl: join(GMETRIX_LTI13_REDIRECT_PATH),
        targetLinkUrl: join(GMETRIX_LTI13_TARGET_LINK_PATH)
    };

};

/**
 * Fixed GMetrix-provided LTI 1.3 Client IDs for LMSes that register the GMetrix tool by Client ID
 * instead of generating their own (e.g. Blackboard, which retrieves the rest of the tool config from
 * the GMetrix developer portal using this Application/Client ID). These are DISPLAY DEFAULTS only:
 * the backend resolves the per-environment value (env var / AppSettings, with this same value as its
 * hardcoded default — DaoPartner.GetManualLtiLmsClientId) and surfaces it as
 * ConsumerSettings.BlackboardClientId, which takes precedence wherever it is available. Never send
 * these values back to the API as the source of truth. Schoology is deliberately absent: its App
 * Center installation needs no Client ID on screen — the GMetrix-owned app's client_id is resolved
 * server-side when the partner saves their Deployment ID.
 *
 * TODO: like the backend's ManualLti_*_ClientId AppSettings keys, consider making this list
 * database-driven/expandable via config in the future instead of hardcoding each LMS here.
 */
export const LMS_LTI13_CLIENT_IDS: Partial<Record<ELms, string>> = {
    [ELms.BLACKBOARD]: 'f45307b0-b4c2-4e78-a703-884ceac9727c'
};

/** Custom-parameter names GMetrix's LTI launch reads when deep linking is not used. */
export const RESOURCE_KEY_PARAM = 'gmxresourcekey';
export const ACCESS_CODE_PARAM = 'accesscode';

export const LMS_LABELS: Record<ELms, string> = {
    [ELms.BLACKBOARD]: 'Blackboard',
    [ELms.CANVAS]: 'Canvas',
    [ELms.D2L]: 'D2L Brightspace',
    [ELms.MOODLE]: 'Moodle',
    [ELms.SCHOOLOGY]: 'Schoology'
};

/**
 * Names the field in each LMS's manual tool configuration that takes the deep-linking (content
 * selection) URL, so the admin knows where to paste it. Localized inline where shown
 * (Integrations.tsx) — only Canvas and Moodle are offered by that form (Schoology installs from
 * the App Center and never sees these fields).
 */

/** LTI protocol version a "Setup Steps:" row documents — the KB articles differ per version. */
export enum ELtiVersion {
    LTI_11 = 'lti11',
    LTI_13 = 'lti13'
}

/**
 * GMetrix Knowledge Base articles for connecting each LMS, linked from the "Setup Steps:" row of
 * each registration section. Keyed by LTI version first because the setup differs between 1.3 and
 * the legacy 1.1 flow, so each section links the article matching its own version.
 */
export const LMS_SETUP_GUIDE_URLS: Record<ELtiVersion, Record<ELms, string>> = {
    [ELtiVersion.LTI_11]: {
        [ELms.BLACKBOARD]: 'https://support.gmetrix.net/en/support/solutions/articles/67000746486-blackboard-lti-1-1-setup',
        [ELms.CANVAS]: 'https://support.gmetrix.net/en/support/solutions/articles/67000746485-canvas-lti-1-1-setup',
        [ELms.D2L]: 'https://support.gmetrix.net/en/support/solutions/articles/67000746488-d2l-brightspace-lti-1-1-setup',
        [ELms.MOODLE]: 'https://support.gmetrix.net/en/support/solutions/articles/67000755599-moodle-lti-1-1-setup',
        [ELms.SCHOOLOGY]: 'https://support.gmetrix.net/en/support/solutions/articles/67000755600-schoology-lti-1-1-setup'
    },
    [ELtiVersion.LTI_13]: {
        [ELms.BLACKBOARD]: 'https://support.gmetrix.net/en/support/solutions/articles/67000755364-blackboard-lti-1-3',
        [ELms.CANVAS]: 'https://support.gmetrix.net/en/support/solutions/articles/67000755376-canvas-lti-1-3',
        [ELms.D2L]: 'https://support.gmetrix.net/en/support/solutions/articles/67000755378-d2l-brightspace-lti-1-3',
        [ELms.MOODLE]: 'https://support.gmetrix.net/en/support/solutions/articles/67000755379-moodle-lti-1-3',
        [ELms.SCHOOLOGY]: 'https://support.gmetrix.net/en/support/solutions/articles/67000755381-schoology-lti-1-3'
    }
};

export type ManualPlatformEndpoints = {
    accessTokenUrl: string;
    authorizeUrl: string;
    issuer: string;
    jwkSetUrl: string;
};

/**
 * Normalizes a customer-entered LMS site address: lowercases the host, keeps any subpath (Moodle
 * often lives under one), drops query/fragment and trailing slashes. Defaults to https when no
 * scheme is typed but PRESERVES an explicit http:// (a plain-http Moodle's issuer is its exact
 * http wwwroot). Returns '' when the input can't be a usable site address. Mirrors
 * PartnersController.NormalizePlatformBaseUrl.
 */
export const normalizePlatformBaseUrl = (input: string): string => {

    const trimmed = input.trim();

    if (!trimmed) return '';

    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

    try {

        const url = new URL(candidate);

        if (!url.hostname.includes('.')) return '';

        const path = url.pathname.replace(/\/+$/, '');
        const port = url.port ? `:${url.port}` : '';

        return `${url.protocol}//${url.hostname.toLowerCase()}${port}${path}`;

    } catch {

        return '';

    }

};

// LTI 1.3 platform-endpoint path suffixes per LMS, appended to the customer's base URL. Both LMSes
// derive uniformly from the entered site address; these are prefill defaults only. For cloud-hosted
// Canvas the real endpoints live on Instructure's shared sso.canvaslms.com (issuer
// https://canvas.instructure.com), so a cloud-Canvas customer edits the prefilled values — the
// same escape hatch that covers self-hosted Canvas, beta/test, and unusual Moodle routing.
const MANUAL_PLATFORM_PATHS: Record<ELms.CANVAS | ELms.MOODLE, { accessToken: string; authorize: string; jwks: string }> = {
    [ELms.CANVAS]: {
        accessToken: '/login/oauth2/token',
        authorize: '/api/lti/authorize_redirect',
        jwks: '/api/lti/security/jwks'
    },
    [ELms.MOODLE]: {
        accessToken: '/mod/lti/token.php',
        authorize: '/mod/lti/auth.php',
        jwks: '/mod/lti/certs.php'
    }
};

/**
 * Default platform endpoints used to PREFILL the manual-registration URL fields from the LMS type
 * and the customer's site address. Both Canvas and Moodle derive from the base URL; the fields stay
 * user-editable and the entered values are what get persisted — the backend only validates that
 * each is an absolute http/https URL. Returns null while the base URL isn't usable yet.
 */
export const buildManualPlatformEndpoints = (lms: ELms.CANVAS | ELms.MOODLE, baseUrl: string): ManualPlatformEndpoints | null => {

    const base = normalizePlatformBaseUrl(baseUrl);

    if (!base) return null;

    const paths = MANUAL_PLATFORM_PATHS[lms];

    return {
        accessTokenUrl: `${base}${paths.accessToken}`,
        authorizeUrl: `${base}${paths.authorize}`,
        issuer: base,
        jwkSetUrl: `${base}${paths.jwks}`
    };

};
